/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

/* eslint-env browser */

import get from 'lodash/get';
import isNil from 'lodash/isNil';

import CommunicatorOrientation from '../orientation/communicator-orientation';
import DriveEnum from '../../drive/drive-enum';
import DriveUtil from '../../drive/drive-util';
import ErrorLevel from '../../error/error-level';
import UpdatePlatformUtil from '../../update/update-platform-util';

/**
 * Komunikátor informací o zařízení pro Ki-Wi Server.
 */
class CommunicatorReport {
  /**
   * Odešle informace o zařízení.
   * @param {ReportInfo} reportInfo Informace o zařízení.
   * @param {string} deviceId Identifikátor Ki-Wi zařízení, které chce komunikovat.
   * @param {string} serverUrl URL adresa Ki-Wi Server pro komunikaci.
   * @returns {Promise.<undefined>} Nic.
   */
  fetch(reportInfo, deviceId, serverUrl) {
    return (
      fetch(`${serverUrl}/api/device/${deviceId}/status`, {
        body: JSON.stringify(
          this.filterReport(this.generateReport(reportInfo))
        ),
        headers: { 'Content-type': 'application/json' },
        method: 'POST'
      })
        // Snížení priority logování pro chyby síťového spojení.
        .catch((error) => {
          error.level = ErrorLevel.Warning;
          throw error;
        })
        .then((response) => {
          // Snížení priority logování pro chyby ze serveru.
          if (!response.ok) {
            const error = new RangeError(
              `Invalid HTTP status "${response.status}".`
            );
            error.level = ErrorLevel.Warning;
            throw error;
          }
        })
    );
  }

  /**
   * Odfiltruje prázdné hodnoty z informací o zařízení.
   * @param {Object} mapped Informace o zařízení ve formátu pro server s prázdnými hodnotami.
   * @private
   * @returns {Object} Informace o zařízení ve formátu pro server.
   */
  filterReport(mapped) {
    return Object.fromEntries(
      Object.entries(mapped).filter(([key, value]) => !isNil(value))
    );
  }

  /**
   * Převede část informací s externími úložišti do formátu pro server.
   * @param {ReportInfo} reportInfo
   * @private
   * @returns {Object} Informace o zařízení ve formátu pro server.
   */
  generateExternalDrives(reportInfo) {
    const { driveInfo } = reportInfo;
    if (isNil(driveInfo)) {
      return {};
    }
    return driveInfo.driveUnitArray
      .filter((driveUnit) => driveUnit.driveEnum === DriveEnum.EXTERNAL)
      .reduce((acc, driveUnit) => {
        const { id } = driveUnit;
        return {
          ...acc,
          [`totalExternal.${id}`]: driveUnit.total,
          [`usedExternal.${id}`]: driveUnit.used
        };
      }, {});
  }

  /**
   * Převede část informací s interním úložištěm do formátu pro server.
   * @param {ReportInfo} reportInfo
   * @private
   * @returns {Object} Informace o zařízení ve formátu pro server.
   */
  generateInternalDrive(reportInfo) {
    const { driveInfo } = reportInfo;
    if (isNil(driveInfo)) {
      return {};
    }
    const driveUnit = DriveUtil.find(driveInfo.driveUnitArray);
    if (isNil(driveUnit)) {
      return {};
    }
    return {
      totalInternal: driveUnit.total,
      usedInternal: driveUnit.used
    };
  }

  /**
   * Převede všechny informace o zařízení do formátu pro server.
   * @param {ReportInfo} reportInfo Informace o zařízení.
   * @private
   * @returns {Object} Informace o zařízení ve formátu pro server.
   */
  generateReport(reportInfo) {
    const { localControlLock, remoteControlLock } = reportInfo;
    return {
      ...this.generateExternalDrives(reportInfo),
      ...this.generateInternalDrive(reportInfo),
      bootTimestamp: reportInfo.bootTimestamp,
      brightness: reportInfo.brightness,
      buildId: reportInfo.buildId,
      contentTimestamp: reportInfo.contentTimestamp,
      controlLocalExceptionsPower: this.isControlPower(localControlLock),
      controlLocalLocked: this.isControlLocked(localControlLock),
      controlRemoteExceptionsPower: this.isControlPower(remoteControlLock),
      controlRemoteLocked: this.isControlLocked(remoteControlLock),
      firmwareVersion: reportInfo.firmwareVersion,
      image: reportInfo.image,
      // Přejmenování "now" na "local".
      localTimestamp: reportInfo.nowTimestamp,
      manufacturer: reportInfo.manufacturer,
      modelName: reportInfo.modelName,
      muted: reportInfo.muted,
      // Orientace v číselníku používaném na serveru.
      orientation: CommunicatorOrientation.generate(reportInfo.orientationEnum),
      // Platforma v číselníku používaném na serveru.
      runtimePlatform: reportInfo.runtimePlatform,
      serialNumber: reportInfo.serialNumber,
      temperature: reportInfo.temperature,
      timezone: reportInfo.timezone,
      totalRam: reportInfo.totalRam,
      // Update platforma v číselníku používaném na serveru.
      updatePlatform: UpdatePlatformUtil.generate(reportInfo.updatePlatform),
      updateUrl: reportInfo.updateUrl,
      usedRam: reportInfo.usedRam,
      version: reportInfo.version,
      volume: reportInfo.volume,
      wiredIpAddress: reportInfo.wiredIpAddress,
      wiredMacAddress: reportInfo.wiredMacAddress,
      wirelessIpAddress: reportInfo.wirelessIpAddress,
      wirelessMacAddress: reportInfo.wirelessMacAddress
    };
  }

  /**
   * Převede zámek ovládání do příznaku `locked`, který se posílá na server.
   * Pokud zámek chybí tak do reportu nevloží nic (např. zařízení nepodporuje).
   * Pokud je zámek přítomný (zařízení omezení podporuje), tak je vždy `locked`
   * nastaveno na `true` nebo `false` dle stavu uzamčení.
   * @param {?ControlLock} controlLock
   * @private
   * @returns {boolean|null}
   */
  isControlLocked(controlLock) {
    return get(controlLock, 'locked', null);
  }

  /**
   * Převede zámek ovládání do příznaku `power`, který se posílá na server.
   * Příznak `power` se odesílá pouze v případě, že je nastaven na `true`.
   * @param {ControlLock} controlLock
   * @private
   * @returns {boolean|null}
   */
  isControlPower(controlLock) {
    const power = get(controlLock, 'exceptions.power');
    if (power) {
      return true;
    }
    return null;
  }
}

export default CommunicatorReport;
